import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import {
  LayoutDashboard, Users, FileText, Package, Calculator,
  Settings, LogOut, Menu, ChevronLeft, ChevronRight,
  Stethoscope, UserPlus, UserCheck, Loader2, Calendar, PieChart, Star, Clock, RefreshCw, PanelLeft, PanelTop, Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useGetMe } from "@/lib/api-client-react";
import FacilityTypePromptModal from "@/components/FacilityTypePromptModal";

export default function ProviderLayout({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [location, setLocation] = useLocation();
  const [navMode, setNavMode] = useState<"left" | "top">(() => {
    return (localStorage.getItem("provider_nav_mode") as "left" | "top") || "left";
  });
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("provider_nav_collapsed") === "true";
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  const [user, setUser] = useState<any>(() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  });
  const { data: meData, refetch: refetchMe, isFetching: isRefetching } = useGetMe();

  const toggleNavMode = (mode: "left" | "top") => {
    setNavMode(mode);
    localStorage.setItem("provider_nav_mode", mode);
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("provider_nav_collapsed", String(next));
      return next;
    });
  };

  useEffect(() => {
    const u = localStorage.getItem("user");
    console.log("[ProviderLayout] Checking user in localStorage:", u ? "EXISTS" : "MISSING");
    if (u) {
      try {
        const parsedUser = JSON.parse(u);
        console.log("[ProviderLayout] Parsed user role:", parsedUser.role, "isImpersonating:", parsedUser.isImpersonating);
        if (parsedUser.role !== 'PROVIDER' && parsedUser.role !== 'CLINIC') {
          console.warn("[ProviderLayout] Role mismatch! Role is:", parsedUser.role, "Redirecting...");
          if (parsedUser.role === 'MEMBER') {
            setLocation("/member/dashboard");
          } else if (parsedUser.role === 'AGENT') {
            setLocation("/agent/dashboard");
          } else {
            setLocation("/login");
          }
          return;
        }
        setUser(parsedUser);
      } catch (e) {
        console.error("[ProviderLayout] Failed to parse user from localStorage:", e);
      }
    } else {
      console.warn("[ProviderLayout] No user found in localStorage, redirecting to /login");
      setLocation("/login");
    }
  }, [setLocation]);

  const currentUser = meData || user;
  const userStatus = (currentUser?.provider?.status || currentUser?.status || "PENDING").toUpperCase();
  const isApproved = userStatus === "APPROVED" || userStatus === "ACTIVE" || Boolean(currentUser?.isImpersonating);
  console.log("[ProviderLayout] currentUser:", currentUser ? currentUser.email : "NONE", "userStatus:", userStatus, "isApproved:", isApproved, "meData:", meData ? "LOADED" : "PENDING");

  const displayName = currentUser?.displayName || (currentUser?.firstName ? `${currentUser.firstName} ${currentUser.lastName}` : "Provider");
  const fallbackChars = displayName.substring(0, 2).toUpperCase();

  const isClinic = currentUser?.provider?.practiceType === "group" || localStorage.getItem("practiceType") === "group";

  if (user && !isApproved) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-4 sm:p-6 relative overflow-hidden transition-colors">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="w-full max-w-lg flex items-center justify-between mb-8 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg shadow-primary/20">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-base text-foreground tracking-tight">StraightDoc</p>
              <p className="text-xs text-muted-foreground">Provider Portal</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              queryClient.clear();
              localStorage.removeItem("user");
              localStorage.removeItem("token");
              localStorage.removeItem("practiceType");
              setLocation("/login");
            }}
            className="text-muted-foreground hover:text-foreground hover:bg-accent rounded-xl text-xs gap-1.5"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </Button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-lg bg-card border border-border rounded-3xl p-6 sm:p-8 shadow-xl z-10 space-y-6 text-card-foreground"
        >
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600 dark:text-amber-400">
                <Clock className="w-8 h-8 animate-pulse" />
              </div>
            </div>
            <h2 className="text-xl font-bold tracking-tight">Account Pending Approval</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your provider profile is currently under review by system administrators.
            </p>
          </div>

          <div className="bg-muted/40 rounded-2xl p-4 space-y-2.5 text-xs">
            <div className="flex items-center justify-between border-b border-border/60 pb-2">
              <span className="text-muted-foreground">Email Address</span>
              <span className="font-semibold text-foreground">{currentUser?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Account Status</span>
              <span className="font-bold text-amber-700 dark:text-amber-300 bg-amber-500/10 px-2.5 py-0.5 rounded-full text-[11px] uppercase">
                {userStatus}
              </span>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <Button
              type="button"
              onClick={async () => {
                const res = await refetchMe();
                if (res.data) {
                  const newStatus = (res.data.provider?.status || res.data.status || "PENDING").toUpperCase();
                  if (newStatus === "APPROVED" || newStatus === "ACTIVE") {
                    localStorage.setItem("user", JSON.stringify(res.data));
                    window.location.reload();
                  }
                }
              }}
              disabled={isRefetching}
              className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl font-bold text-sm gap-2 shadow-md shadow-primary/20 transition-all"
            >
              {isRefetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {isRefetching ? "Checking Status..." : "Check Approval Status"}
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  const navItems = [
    { href: "/provider/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { href: "/provider/appointments", icon: Calendar, label: "Appointments" },
    { href: "/provider/patients", icon: Users, label: "Patients" },
    { href: "/provider/cpt-codes", icon: FileText, label: "Billing Codes" },
    { href: "/provider/bundles", icon: Package, label: "Bundles" },
    { href: "/provider/cost-estimator", icon: Calculator, label: "Good Faith Estimate" },
    { href: "/provider/saved-estimates", icon: FileText, label: "Estimates" },
    { href: "/provider/check-member", icon: UserCheck, label: "Verify Membership" },
    { href: "/provider/team", icon: UserPlus, label: "My Team & Groups" },
    { href: "/provider/reports", icon: PieChart, label: "Reports" },
    { href: "/provider/reviews", icon: Star, label: "Reviews" },
    { href: "/provider/settings", icon: Settings, label: "Settings" },
    { href: "/provider/notifications", icon: Bell, label: "Notifications" },
  ];

  const handleSignOut = () => {
    queryClient.clear();
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("practiceType");
    setLocation("/");
  };

  const SidebarContent = ({ isMobile = false }: { isMobile?: boolean } = {}) => {
    const isCollapsed = isMobile ? false : collapsed;
    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-4 h-4 text-white" />
            </div>
            {!isCollapsed && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <p className="font-bold text-sm text-foreground">StraightDoc</p>
                <p className="text-xs text-muted-foreground">Provider Portal</p>
              </motion.div>
            )}
          </div>
          {!isMobile && (
            <button
              type="button"
              onClick={toggleCollapsed}
              className="p-1 text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent transition"
              title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          )}
        </div>

        {!isCollapsed && (
          <div className="px-3 pt-3">
            <div className="p-1 bg-muted/60 rounded-xl flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
              <button
                type="button"
                onClick={() => toggleNavMode("left")}
                className={`flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded-lg transition ${
                  navMode === "left"
                    ? "bg-background shadow-2xs text-primary font-bold"
                    : "hover:text-foreground"
                }`}
              >
                <PanelLeft className="w-3 h-3" /> Left
              </button>
              <button
                type="button"
                onClick={() => toggleNavMode("top")}
                className={`flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded-lg transition ${
                  navMode === "top"
                    ? "bg-background shadow-2xs text-primary font-bold"
                    : "hover:text-foreground"
                }`}
              >
                <PanelTop className="w-3 h-3" /> Top
              </button>
            </div>
          </div>
        )}

        <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto min-h-0">
          {navItems.map((item) => {
            const active = location === item.href;
            return (
              <Link key={item.href} href={item.href} onClick={() => setMobileOpen(false)}>
                <motion.div
                  whileHover={{ x: 2 }}
                  className={`flex items-center ${isCollapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"} rounded-xl cursor-pointer transition-colors ${active
                      ? "bg-primary text-primary-foreground shadow-sm font-semibold"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    }`}
                  title={isCollapsed ? item.label : undefined}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!isCollapsed && (
                    <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs font-medium">
                      {item.label}
                    </motion.span>
                  )}
                </motion.div>
              </Link>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-border">
          <div className={`flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-accent cursor-pointer transition-colors ${isCollapsed ? "justify-center" : ""}`}>
            <Avatar className="w-7 h-7 flex-shrink-0">
              <AvatarFallback className="bg-secondary/10 text-secondary text-xs font-semibold">{fallbackChars}</AvatarFallback>
            </Avatar>
            {!isCollapsed && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{displayName}</p>
                <p className="text-[11px] text-muted-foreground truncate">{user?.role || "Provider"}</p>
              </motion.div>
            )}
          </div>
          <div
            onClick={handleSignOut}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-destructive/10 cursor-pointer transition-colors text-muted-foreground hover:text-destructive mt-1 ${isCollapsed ? "justify-center" : ""}`}
            title={isCollapsed ? "Sign Out" : undefined}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!isCollapsed && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs font-medium">Sign Out</motion.span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background text-foreground">
      {/* Top Navigation Mode Bar (REDESIGNED 2-ROW HEADER MATCHING ADMIN PORTAL) */}
      {navMode === "top" && (
        <header className="shrink-0 z-30 bg-card border-b border-border shadow-2xs">
          {/* Row 1: Brand, Mobile Hamburger, Layout Selector, User Info */}
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4 border-b border-border">
            <div className="flex items-center gap-3 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(true)}
                className="lg:hidden"
                title="Open navigation menu"
              >
                <Menu className="w-5 h-5 text-foreground" />
              </Button>
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Stethoscope className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-sm text-foreground">StraightDoc</span>
              <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider border-l border-border pl-3 hidden sm:inline">
                Provider Portal
              </span>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <div className="p-1 bg-muted/60 rounded-xl flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hidden sm:flex">
                <button
                  type="button"
                  onClick={() => toggleNavMode("left")}
                  className="flex items-center gap-1.5 py-1 px-2.5 rounded-lg hover:text-foreground transition"
                >
                  <PanelLeft className="w-3.5 h-3.5" /> Left Nav
                </button>
                <button
                  type="button"
                  onClick={() => toggleNavMode("top")}
                  className="flex items-center gap-1.5 py-1 px-2.5 rounded-lg bg-background shadow-2xs text-primary font-bold"
                >
                  <PanelTop className="w-3.5 h-3.5" /> Top Nav
                </button>
              </div>

              <div className="flex items-center gap-2 pl-2 border-l sm:border-l border-border">
                <Avatar className="w-7 h-7">
                  <AvatarFallback className="bg-secondary/10 text-secondary text-xs font-semibold">{fallbackChars}</AvatarFallback>
                </Avatar>
                <span className="text-xs font-semibold text-foreground hidden md:inline">
                  {displayName}
                </span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleSignOut} title="Sign Out">
                <LogOut className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          </div>

          {/* Row 2: Centered Sub-Navigation Items (Desktop only) */}
          <div className="hidden lg:flex max-w-[1600px] mx-auto px-4 sm:px-6 py-2.5 items-center justify-center">
            <nav className="flex flex-wrap items-center justify-center gap-1.5 py-0.5 px-0 max-w-full">
              {navItems.map((item) => {
                const active = location === item.href;
                return (
                  <Link key={item.href} href={item.href}>
                    <div
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap cursor-pointer transition-all shrink-0 ${
                        active
                          ? "bg-primary text-primary-foreground shadow-2xs"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      }`}
                    >
                      <item.icon className="w-3.5 h-3.5" />
                      <span>{item.label}</span>
                    </div>
                  </Link>
                );
              })}
            </nav>
          </div>
        </header>
      )}

      {/* Main Body with Sidebar or Full Width Top Nav */}
      <div className="flex flex-1 overflow-hidden relative min-h-0">
        {navMode === "left" && (
          <aside
            className={`hidden lg:flex flex-col bg-card border-r border-border transition-all duration-300 h-full shrink-0 ${collapsed ? "w-20" : "w-60"
              }`}
          >
            {SidebarContent()}
          </aside>
        )}

        {/* Mobile Header when sidebar mode */}
        {navMode === "left" && (
          <header className="lg:hidden h-14 bg-card border-b border-border flex items-center justify-between px-4 fixed top-0 left-0 right-0 z-20">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} title="Open navigation menu">
                <Menu className="w-5 h-5 text-foreground" />
              </Button>
              <div className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-primary" />
                <span className="font-bold text-sm">StraightDoc</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-secondary/10 text-secondary text-xs font-semibold">{fallbackChars}</AvatarFallback>
              </Avatar>
            </div>
          </header>
        )}

        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                onClick={() => setMobileOpen(false)}
              />
              <motion.aside
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ type: "spring", damping: 25 }}
                className="fixed left-0 top-0 bottom-0 w-72 bg-card border-r border-border z-50 lg:hidden"
              >
                {SidebarContent({ isMobile: true })}
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        <main className={`flex-1 overflow-y-auto min-w-0 h-full ${navMode === "left" ? "pt-14 lg:pt-0" : ""}`}>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="px-4 sm:px-6 md:px-8 py-6 max-w-[1600px] mx-auto w-full"
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
